import type { ErrorEnvelope } from './types'

/**
 * A failed BFF call, carrying the envelope's code and request id.
 *
 * `requestId` is the reason this is a class rather than a bare message: the
 * BFF and the Go API both log it, so a user reporting "it said something went
 * wrong" is actionable only if the UI can show them that id.
 */
export class BffError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string | null

  constructor(status: number, code: string, message: string, requestId: string | null) {
    super(message)
    this.name = 'BffError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }
}

/** True when the session is gone — the caller should send the user to the
 *  landing screen rather than showing an error.
 *
 *  `organisation_disabled` belongs here despite being a 403: the BFF ends the
 *  session as it forwards that refusal (FR-11, AB-31), so by the time this is
 *  read the cookie is already cleared and there is no session left to keep the
 *  shell on screen for. Treating it as an ordinary error would leave the user
 *  clicking around a portal that can no longer answer them, the same defect
 *  MPAY-32193 fixed for expiry. */
export const isUnauthenticated = (e: unknown): boolean =>
  e instanceof BffError &&
  (e.status === 401 || e.code === 'unauthenticated' || e.code === 'organisation_disabled')

/** True when CSRF rejected the call, which in practice means the session was
 *  replaced under us (a second tab logged in as someone else). */
export const isCsrfFailure = (e: unknown): boolean =>
  e instanceof BffError && e.code === 'csrf_failed'

function isEnvelope(value: unknown): value is ErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  const error = v['error']
  return typeof error === 'object' && error !== null && typeof (error as Record<string, unknown>)['code'] === 'string'
}

/** Builds a BffError from a response, falling back to the status when the
 *  body is not an envelope (a proxy or load balancer error page, say). */
export async function errorFromResponse(response: Response): Promise<BffError> {
  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    // Not JSON — fall through to the status-only error below.
  }
  if (isEnvelope(body)) {
    return new BffError(response.status, body.error.code, body.error.message, body.meta?.request_id ?? null)
  }
  return new BffError(response.status, 'unexpected_response', `request failed with status ${response.status}`, null)
}
